import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { useVisitorStats } from '../../../hooks/useVisitorTracking';

const VisitorStatsSection = () => {
  const [stats, setStats] = useState({
    today: { daily_visits: 0, unique_visitors: 0, unique_ips: 0 },
    thisMonth: { monthly_visits: 0, unique_visitors: 0, unique_ips: 0 },
    total: { total_visits: 0, total_unique_visitors: 0, total_unique_ips: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { getTodayStats, getThisMonthStats, getTotalStats } = useVisitorStats();

  useEffect(() => {
    let isMounted = true;
    
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);

        const [todayStats, thisMonthStats, totalStats] = await Promise.all([
          getTodayStats(),
          getThisMonthStats(),
          getTotalStats()
        ]);

        if (isMounted) {
          setStats({
            today: todayStats,
            thisMonth: thisMonthStats,
            total: totalStats
          });
        }
      } catch (err) {
        console.error('Error fetching visitor stats:', err);
        if (isMounted) {
          setError('ไม่สามารถโหลดสถิติได้');
          // Set fallback data
          setStats({
            today: { daily_visits: 0, unique_visitors: 0, unique_ips: 0 },
            thisMonth: { monthly_visits: 0, unique_visitors: 0, unique_ips: 0 },
            total: { total_visits: 0, total_unique_visitors: 0, total_unique_ips: 0 }
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchStats();
    
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array to prevent infinite requests

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString('th-TH');
  };

  const statsCards = [
    {
      id: 1,
      title: "เข้าชมวันนี้",
      value: formatNumber(stats.today.daily_visits),
      unit: "ครั้ง",
      icon: "Calendar",
      color: "text-primary",
      bgColor: "bg-primary/10",
      description: `ผู้เยี่ยมชม: ${formatNumber(stats.today.unique_visitors)} คน`
    },
    {
      id: 2,
      title: "เข้าชมเดือนนี้",
      value: formatNumber(stats.thisMonth.monthly_visits),
      unit: "ครั้ง",
      icon: "BarChart3",
      color: "text-secondary",
      bgColor: "bg-secondary/10",
      description: `ผู้เยี่ยมชม: ${formatNumber(stats.thisMonth.unique_visitors)} คน`
    },
    {
      id: 3,
      title: "เข้าชมทั้งหมด",
      value: formatNumber(stats.total.total_visits),
      unit: "ครั้ง",
      icon: "TrendingUp",
      color: "text-success",
      bgColor: "bg-success/10",
      description: `ผู้เยี่ยมชม: ${formatNumber(stats.total.total_unique_visitors)} คน`
    },
    {
      id: 4,
      title: "IP Address",
      value: formatNumber(stats.total.total_unique_ips),
      unit: "IP",
      icon: "Globe",
      color: "text-accent",
      bgColor: "bg-accent/10",
      description: "IP Address ที่แตกต่างกัน"
    }
  ];

  if (loading) {
    return (
      <section className="py-12 lg:py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              สถิติการเข้าชมเว็บไซต์
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              กำลังโหลดข้อมูลสถิติการเข้าชม...
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-card rounded-lg p-6 shadow-government animate-pulse">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-lg bg-muted"></div>
                  <div className="h-6 bg-muted rounded mb-2"></div>
                  <div className="h-8 bg-muted rounded mb-2"></div>
                  <div className="h-4 bg-muted rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-12 lg:py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              สถิติการเข้าชมเว็บไซต์
            </h2>
            <div className="bg-error/10 border border-error/20 rounded-lg p-6 max-w-md mx-auto">
              <Icon name="AlertTriangle" size={48} className="text-error mx-auto mb-4" />
              <p className="text-error font-medium">{error}</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 lg:py-16 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
            สถิติการเข้าชมเว็บไซต์
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            ข้อมูลสถิติการเข้าชมเว็บไซต์ที่ทำการปกครองอำเภอเทิง
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statsCards.map((stat) => (
            <div
              key={stat.id}
              className="bg-card rounded-lg p-6 shadow-government hover:shadow-government-lg transition-government"
            >
              <div className="text-center">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                  <Icon name={stat.icon} size={32} className={stat.color} />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {stat.title}
                </h3>
                <div className="mb-2">
                  <span className="text-3xl font-bold text-foreground">
                    {stat.value}
                  </span>
                  <span className="text-sm text-muted-foreground ml-1">
                    {stat.unit}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {stat.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="bg-card rounded-lg p-6 shadow-government">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="flex items-center justify-center mb-2">
                <Icon name="Clock" size={20} className="text-primary mr-2" />
                <span className="text-sm font-medium text-foreground">อัปเดตล่าสุด</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {new Date().toLocaleString('th-TH', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <div>
              <div className="flex items-center justify-center mb-2">
                <Icon name="Calendar" size={20} className="text-secondary mr-2" />
                <span className="text-sm font-medium text-foreground">เริ่มติดตาม</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {stats.total.first_visit_date 
                  ? new Date(stats.total.first_visit_date).toLocaleDateString('th-TH')
                  : 'ยังไม่มีข้อมูล'
                }
              </p>
            </div>
            <div>
              <div className="flex items-center justify-center mb-2">
                <Icon name="Activity" size={20} className="text-success mr-2" />
                <span className="text-sm font-medium text-foreground">สถานะระบบ</span>
              </div>
              <p className="text-sm text-success font-medium">
                ทำงานปกติ
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisitorStatsSection;
